import { useState } from "react";
import { useApp } from "../app/AppContext.jsx";
import { pickLabel } from "../i18n/index.js";
import { money, moneyBand } from "../lib/format.js";
import { COUNTRIES } from "../i18n/index.js";

/**
 * The landed-price breakdown.
 *
 * Two things make this honest rather than a marketing number, and both are deliberate:
 *
 *  1. Every cost group is visible. Import quoting is where buyers get burned — a low
 *     headline followed by "oh, and the homologation" at the port — so the total is broken
 *     down by group: the car, export, shipping, customs and taxes, homologation, ApexCars.
 *     Not fee by fee; that was more detail than a buyer can use.
 *  2. The range is shown, not hidden. A TÜV Vollabnahme is genuinely €600–6,000 depending
 *     on the car; collapsing that to one number would be a lie with a decimal point.
 *
 * The `confidence` banner is not decoration either — while rulesets are still being
 * verified it says so, in the buyer's language, above the number.
 */
export default function QuotePanel({ quote, comparison, onCountryChange, onOptionChange, options = {}, onEnquire }) {
  const { t, lang, country } = useApp();
  const [showCompare, setShowCompare] = useState(false);

  if (!quote) return <div className="quote" aria-busy="true" aria-label={t("common.loading")}><div className="quote__head"><div className="skeleton" style={{ height: 20, width: "70%" }} /><div className="skeleton" style={{ height: 40, marginTop: 16 }} /></div><div className="quote__body">{Array.from({ length: 6 }, (_, i) => <div key={i} className="skeleton" style={{ height: 32, marginTop: 16 }} />)}</div></div>;

  const ccy = quote.currency;
  const confKey = `car.confidence.${quote.confidence}`;
  const confClass = quote.confidence === "researched" ? "badge--ok" : quote.confidence === "estimate" ? "badge--warn" : "badge--plain";

  return (
    <aside className="quote">
      <div className="quote__head">
        <div className="quote__heading">
          <span className="label">{t("car.landedIn", { country: COUNTRIES.find((c) => c.code === country)?.name[lang] ?? country })}</span>
          <span className={`badge ${confClass}`} title={t(`${confKey}Help`)}>{t(confKey)}</span>
        </div>

        <p className="tiny muted quote__confidence">{t(`${confKey}Help`)}</p>
        <div className="quote__total">{money(quote.total.typical, ccy, country)}</div>
        {quote.total.max > quote.total.min && (
          <div className="quote__range mono">{moneyBand(quote.total, ccy, country, { alwaysRange: true })}</div>
        )}
        <p className="tiny muted" style={{ marginTop: 8, lineHeight: 1.6 }}>{t("car.estimateNote")}</p>

        {quote.leadTimeDays?.typical > 0 && (
          <p className="small muted" style={{ marginTop: 10 }}>
            {t("car.leadTime")}: <strong style={{ color: "var(--text)" }}>{t("car.leadTimeDays", { n: Math.round(quote.leadTimeDays.typical) })}</strong>
          </p>
        )}
      </div>

      <div className="quote__body">
        <p className="label" style={{ display: "block", padding: "14px 0 2px" }}>{t("car.breakdown")}</p>

        {/* One line per group, one figure per line. The owner's call: the fee-by-fee list, each
            with its own range and note, read as noise to a buyer who wants the landed price.
            The detail is still in the quote object and in the desk's copy of every enquiry. */}
        {quote.groups.map((g) => (
          <div key={g.group} className="qgroup">
            <div className="qgroup__head">
              <span className="qgroup__label">{pickLabel(g.label, lang)}</span>
              <span className="qgroup__amt mono">{money(g.amount.typical, ccy, country)}</span>
            </div>
          </div>
        ))}

        {/* Options that genuinely move the number — surfaced rather than buried, because
            "can the manufacturer issue a COC?" is worth thousands. */}
        {onOptionChange && (
          <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 14 }}>
            {(quote?.freightModes?.length ?? 2) > 1 && <div className="field">
              <label htmlFor="freight">{t("car.freight")}</label>
              <select
                id="freight" className="select" value={options.freight || "container_shared"}
                onChange={(e) => onOptionChange({ ...options, freight: e.target.value })}
              >
                <option value="roro">{t("car.freight.roro")}</option>
                <option value="container_shared">{t("car.freight.container_shared")}</option>
                <option value="container_exclusive">{t("car.freight.container_exclusive")}</option>
              </select>
            </div>}

            <label className="fopt" style={{ alignItems: "flex-start", gap: 10 }}>
              <input
                type="checkbox" style={{ marginTop: 4 }}
                checked={!!options.coc}
                onChange={(e) => onOptionChange({ ...options, coc: e.target.checked })}
              />
              <span>
                <span style={{ display: "block", fontSize: 13 }}>{t("car.coc")}</span>
                <span className="tiny muted" style={{ display: "block", marginTop: 4, lineHeight: 1.55 }}>{t("car.cocHelp")}</span>
              </span>
            </label>
          </div>
        )}

        {/* Same car, four countries. Often a five-figure difference, and the single most
            useful thing the portal can tell a buyer who can register anywhere. */}
        {comparison?.ranked?.length > 1 && (
          <div style={{ marginTop: 20 }}>
            <button className="btn btn--ghost btn--sm" style={{ width: "100%" }} aria-expanded={showCompare} onClick={() => setShowCompare((s) => !s)}>
              {t("car.compare")}
            </button>
            {showCompare && (
              <>
                <p className="tiny muted" style={{ margin: "12px 0 10px", lineHeight: 1.6 }}>{t("car.compareNote")}</p>
                <div className="compare">
                  {comparison.ranked.map((r, i) => {
                    const meta = COUNTRIES.find((c) => c.code === r.country);
                    return (
                      <button
                        type="button" aria-pressed={r.country === country}
                        key={r.country}
                        className={`compare__row${r.country === country ? " is-active" : ""}`}
                        onClick={() => onCountryChange?.(r.country)}
                      >
                        <span className="compare__flag">{meta?.flag}</span>
                        <span>
                          {meta?.name[lang] ?? r.country}
                          {i === 0 && <span className="compare__best" style={{ marginLeft: 8 }}>{t("car.cheapest")}</span>}
                        </span>
                        <span className="compare__amt">{money(r.total.typical, r.currency, r.country)}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* Conversion work a non-EU-spec car *might* need.
            Shown, but deliberately outside the total: summing all of it inflated homologation
            from ~2,000 to over 60,000 on a car that in practice needs none of it. Whatever the
            inspection actually finds is quoted per car. */}
        {quote.advisory?.length > 0 && (
          <details style={{ marginTop: 20 }}>
            <summary className="small" style={{ cursor: "pointer", color: "var(--muted)" }}>
              {t("car.advisory")}
              {quote.advisoryTotal && (
                <span className="tiny muted" style={{ marginLeft: 8 }}>
                  · {t("car.advisoryUpTo", { amount: money(quote.advisoryTotal.max, ccy, country) })}
                </span>
              )}
            </summary>
            <p className="tiny muted" style={{ margin: "10px 0 12px", lineHeight: 1.6 }}>{t("car.advisoryNote")}</p>
            <div className="qgroup__items">
              {quote.advisory.map((a) => (
                <div key={a.id} className="qitem">
                  <span>
                    {pickLabel(a.label, lang)}
                    {/* Most of this list is work a car probably does not need. One item is
                        work it certainly does, and saying so is the difference between a
                        disclosure and a shrug. */}
                    {a.certain && <span className="badge badge--warn" style={{ marginLeft: 8 }}>{t("car.required")}</span>}
                  </span>
                  <span className="qitem__amt">
                    {!a.amount
                      ? <span className="muted">{t("car.quotedPerCar")}</span>
                      : a.amount.max > a.amount.min
                        ? moneyBand(a.amount, ccy, country, { alwaysRange: true })
                        : money(a.amount.typical, ccy, country)}
                  </span>
                  {a.note && <span className="qitem__note">{pickLabel(a.note, lang)}</span>}
                </div>
              ))}
            </div>
          </details>
        )}

        {/* What the source did not publish and we therefore guessed. Shown, not hidden:
            an unknown CO2 figure on a V8 into France is a five-figure unknown. */}
        {quote.assumptions?.length > 0 && (
          <details style={{ marginTop: 18 }}>
            <summary className="small muted" style={{ cursor: "pointer" }}>{t("car.assumptions")}</summary>
            <p className="tiny muted" style={{ margin: "10px 0", lineHeight: 1.6 }}>{t("car.assumptionsNote")}</p>
            <ul style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              {quote.assumptions.map((a) => (
                <li key={a.field} className="tiny muted">
                  <strong style={{ color: "var(--text)" }}>{a.field}</strong>
                  {a.assumed != null && <> = {String(a.assumed)}</>} — {a.reason}
                </li>
              ))}
            </ul>
          </details>
        )}

        {onEnquire && (
          <button className="btn btn--gold" style={{ width: "100%", marginTop: 22 }} onClick={onEnquire}>
            {t("car.enquire")}
          </button>
        )}
      </div>
    </aside>
  );
}
